import { VJSBridgeFront } from './VJSBridgeFront';
import { SystemType } from '@/types/env';
import { getSystemType } from '@/utils/index';
import { MediaType, Media, ExportShareInfo, ShareVideoSourceType } from '@/types/media';

export enum OperationType {
  Request = 'request',
  Toast = 'toast',
  Jump = 'jump',
  GetNativeInfo = 'get-native-info',
  SetGoBackType = 'set-go-back-type',
  ApplyForChannelAuthorization = 'apply-for-channel-authorization',
  OpenNewWebview = 'open-new-webview',
  ChooseMedia = 'choose-media',
  ExportMediaShare = 'export-media-share',
  ChooseMediaExportShare = 'choose-media-export-share',
}
export enum ILinkType {
  Main,
  SMS,
}
export enum GoBackType {
  RoutePop = 1,
  ShutOff,
}
export enum NativeInfoCmd {
  GetUserChannelData = 'GetUserChannelData',
  GetInitialGlobalData = 'GetInitialGlobalData',
  GetNetworkType = 'GetNetworkType',
}

export interface OperationData {
  type: OperationType;
  payload: OperationPayload;
}

type OperationPayload =
  | OperationPayloadRequestILinkWithId
  | OperationPayloadToast
  | OperationPayloadJump
  | OperationPayloadOpenNewWebView
  | OperationPayloadGoBack
  | OperationPayloadGetNativeInfoWithId
  | OperationPayloadChannelAuthorizationWithId;

interface ILinkConfig {
  url: string; // ilink url
  cgiName: string; // ilink cgiName
  cmdId: number; // ilink cmdid
}
export interface OperationPayloadRequestILink extends ILinkConfig {
  data: OperationPayloadRequestInterior;
}

interface OperationPayloadRequestILinkWithId extends OperationPayloadRequestILink {
  callbackId?: number; // 回调 ID，用于 Native 调用结束后通知前端执行后续操作
}

export interface OperationPayloadRequestInterior {
  cmd: string;
  [key: string]: any;
}

export interface OperationPayloadToast {
  description: string; // 弹窗的描述
  confirmText: string; // 确定按钮的文案
  confirmCallback?: () => void; // 确定按钮点击后需要前端执行的 callback
  cancelText?: string; // 取消按钮的文案
  cancelCallback?: () => void; // 取消按钮点击后需要前端执行的 callback
}

export type OperationPayloadToastWithId = Omit<
OperationPayloadToast,
'confirmCallback' | 'cancelCallback'
> & {
  confirmCallbackId?: number;
  cancelCallbackId?: number;
};

export interface OperationPayloadJump {
  url: string; // 目标 URL Scheme
}
export interface OperationPayloadOpenNewWebView {
  url: string; // 目标 URL Scheme
}

export interface OperationPayloadGoBack {
  type: GoBackType;
}
export interface OperationPayloadChannelAuthorization {
  succeedCallback: () => void;
  failCallback: () => void;
}
export interface OperationPayloadChannelAuthorizationWithId {
  succeedCallbackId: number;
  failCallbackId: number;
}
export interface OperationPayloadGetNativeInfo {
  cmd: NativeInfoCmd;
  data?: Record<string, any>;
}
export interface OperationPayloadGetNativeInfoWithId extends OperationPayloadGetNativeInfo {
  callbackId?: number;
}

export interface OperationPayloadChooseMedia {
  mediaType?: string[];
  count?: number;
  maxSize?: number;
  needCompression?: boolean;
  compressionRate?: number;
}

export interface OperationPayloadChooseMediaWithId {
  mediaType: string[];
  count: number;
  maxSize: number;
  needCompression: boolean;
  compressionRate: number;
  succeedCallbackId: number;
  failCallbackId: number;
}

export type OperationPayloadExportMediaShare = {
  media: Media[]
} & ExportShareInfo;

export type OperationPayloadChooseMediaExportShare = {
  mediaType?: MediaType[];
  count?: number;
} & ExportShareInfo;

interface VJSBridgeClient {
  postMessage: (operationData: string | Record<string, any>) => {};
}

const CLIENT_BRIDGE_WINDOW_KEY = 'VJSBridgeClient';
const ILINK_CONFIG: { [key in ILinkType]: ILinkConfig } = {
  [ILinkType.Main]: {
    url: '/ilink/ilinkapp/vue/creator-video',
    cgiName: 'mmvvideocreator',
    cmdId: 1626,
  },
  [ILinkType.SMS]: {
    url: '/ilink/ilinkapp/vue/sms',
    cgiName: 'mmvsms',
    cmdId: 4552,
  },
};

export class VJSBridge {
  static instance: VJSBridge;
  public static getVJSBridge() {
    if (!this.instance) {
      this.instance = new VJSBridge();
    }
    return this.instance;
  }
  bridgeClient: VJSBridgeClient | null = null;
  constructor() {
    if (!VJSBridge.instance) {
      this.setupVJSBridgeClient();
      VJSBridge.instance = this;
    }
    return VJSBridge.instance;
  }

  public setupVJSBridgeClient() {
    const systemType = getSystemType();
    console.log('System detected as: ', SystemType[systemType]);
    switch (systemType) {
      case SystemType.Android:
        this.bridgeClient = this.getAndroidBridgeClient();
        break;
      case SystemType.iOS:
        this.bridgeClient = this.getIOSBridgeClient();
        break;
      default:
        this.bridgeClient = null;
        console.error('Error occurs. Invalid system type detected');
    }
    if (!this.bridgeClient) {
      console.error('Error occurs. Bridge client is invalid. Expect for lazy setting up procedure.');
    }
  }

  public sendRequestOperation(
    payload: OperationPayloadRequestInterior,
    iLinkType: ILinkType,
    callback?: (data?: any) => void,
  ) {
    const validPayload: OperationPayloadRequestILinkWithId = {
      data: payload,
      ...ILINK_CONFIG[iLinkType],
    };
    if (callback) {
      const callbackId = VJSBridgeFront.registerCallback(callback);
      validPayload.callbackId = callbackId;
    }
    return this.sendOperation({
      type: OperationType.Request,
      payload: validPayload,
    });
  }

  // public sendToastOperation(payload: OperationPayloadToast) {
  //   const { confirmCallback, cancelCallback, ...payloadBase } = payload;
  //   const validPayload: OperationPayloadToastWithId = {
  //     ...payloadBase,
  //   };
  //   Dialog.confirm({
  //     message: validPayload.description,
  //     confirmButtonText: validPayload.confirmText,
  //     cancelButtonText: validPayload.cancelText,
  //     confirmButtonColor: '#07c160',
  //     cancelButtonColor: '#07c160',
  //   })
  //     .then(confirmCallback)
  //     .catch(cancelCallback);
  // }

  public sendToastOperation(payload: OperationPayloadToast) {
    const { confirmCallback, cancelCallback, ...payloadBase } = payload;
    let confirmCallbackId, cancelCallbackId;
    if (confirmCallback) {
      confirmCallbackId = VJSBridgeFront.registerCallback(confirmCallback);
    }
    if (cancelCallback) {
      cancelCallbackId = VJSBridgeFront.registerCallback(cancelCallback);
    }
    const validPayload: OperationPayloadToastWithId = {
      ...payloadBase,
    };
    confirmCallbackId && (validPayload.confirmCallbackId = confirmCallbackId);
    cancelCallbackId && (validPayload.cancelCallbackId = cancelCallbackId);
    return this.sendOperation({
      type: OperationType.Toast,
      payload: validPayload,
    });
  }

  // public sendToastOperationSync(payload: OperationPayloadToast) {
  //   const { ...payloadBase } = payload;
  //   const validPayload: OperationPayloadToastWithId = {
  //     ...payloadBase,
  //   };
  //   return Dialog.confirm({
  //     message: validPayload.description,
  //     confirmButtonText: validPayload.confirmText,
  //     cancelButtonText: validPayload.cancelText,
  //     confirmButtonColor: '#07c160',
  //     cancelButtonColor: '#07c160',
  //   });
  // }

  public async sendToastOperationSync(payload: OperationPayloadToast) {
    return new Promise((resolve, reject) => {
      const { confirmCallback, cancelCallback, ...payloadBase } = payload;
      const confirmCallbackId = VJSBridgeFront.registerCallback((...args) => {
        confirmCallback && confirmCallback();
        resolve(...args);
      });
      const cancelCallbackId = VJSBridgeFront.registerCallback((...args) => {
        cancelCallback && cancelCallback();
        reject(...args);
      });
      const validPayload: OperationPayloadToastWithId = {
        ...payloadBase,
        confirmCallbackId,
        cancelCallbackId,
      };
      return this.sendOperation({
        type: OperationType.Toast,
        payload: validPayload,
      });
    });
  }

  public sendJumpOperation(payload: OperationPayloadJump) {
    return this.sendOperation({
      type: OperationType.Jump,
      payload,
    });
  }

  public sendOpenNewWebViewOperation(payload: OperationPayloadOpenNewWebView) {
    return this.sendOperation({
      type: OperationType.OpenNewWebview,
      payload,
    });
  }

  public sendSetGoBackOperation(payload: OperationPayloadGoBack) {
    return this.sendOperation({
      type: OperationType.SetGoBackType,
      payload,
    });
  }

  public sendGetNativeInfoOperation(
    payload: OperationPayloadGetNativeInfo,
    callback?: (data: any) => void,
  ) {
    const validPayload: OperationPayloadGetNativeInfoWithId = {
      ...payload,
    };
    if (callback) {
      const callbackId = VJSBridgeFront.registerCallback(callback);
      validPayload.callbackId = callbackId;
    }
    return this.sendOperation({
      type: OperationType.GetNativeInfo,
      payload: validPayload,
    });
  }

  public sendApplyChannelAuthorizationOperation(payload: OperationPayloadChannelAuthorization) {
    const { succeedCallback, failCallback } = payload;
    const succeedCallbackId = VJSBridgeFront.registerCallback(succeedCallback);
    const failCallbackId = VJSBridgeFront.registerCallback(failCallback);
    const validPayload: OperationPayloadChannelAuthorizationWithId = {
      succeedCallbackId,
      failCallbackId,
    };
    return this.sendOperation({
      type: OperationType.ApplyForChannelAuthorization,
      payload: validPayload,
    });
  }

  public chooseMedia(payload: OperationPayloadChooseMedia) {
    const {
      mediaType = [MediaType.Image, MediaType.Video],
      count = 1,
      maxSize = 10 * 1024,
      needCompression = true,
      compressionRate = 0.5,
    } = payload;
    return new Promise<Media[]>((resolve, reject) => {
      const succeedCallbackId = VJSBridgeFront.registerCallback(resolve);
      const failCallbackId = VJSBridgeFront.registerCallback(reject);
      const validPayload: OperationPayloadChooseMediaWithId = {
        mediaType,
        count,
        maxSize,
        needCompression,
        compressionRate,
        succeedCallbackId,
        failCallbackId,
      };
      this.sendOperation({
        type: OperationType.ChooseMedia,
        payload: validPayload,
      });
    });
  }

  // 客户端暂未实现
  public exportMediaShare(payload: OperationPayloadExportMediaShare) {
    const {
      media,
      ...exportShareInfo
    } = payload;
    return new Promise((resolve, reject) => {
      const succeedCallbackId = VJSBridgeFront.registerCallback(resolve);
      const failCallbackId = VJSBridgeFront.registerCallback(reject);
      const validPayload = {
        media,
        ...exportShareInfo,
        videoSourceType: exportShareInfo.videoSourceType || ShareVideoSourceType.Tutorial,
        succeedCallbackId,
        failCallbackId,
      };
      this.sendOperation({
        type: OperationType.ExportMediaShare,
        payload: validPayload,
      });
    });
  }

  public chooseMediaExportShare(payload: OperationPayloadChooseMediaExportShare) {
    const {
      mediaType = [MediaType.Image, MediaType.Video],
      count = 1,
      ...exportShareInfo
    } = payload;
    return new Promise((resolve, reject) => {
      const succeedCallbackId = VJSBridgeFront.registerCallback(resolve);
      const failCallbackId = VJSBridgeFront.registerCallback(reject);
      const validPayload = {
        mediaType,
        count,
        ...exportShareInfo,
        videoSourceType: exportShareInfo.videoSourceType || ShareVideoSourceType.Tutorial,
        succeedCallbackId,
        failCallbackId,
      };
      this.sendOperation({
        type: OperationType.ChooseMediaExportShare,
        payload: validPayload,
      });
    });
  }

  private getAndroidBridgeClient(): VJSBridgeClient {
    return (window as any)[CLIENT_BRIDGE_WINDOW_KEY];
  }

  private getIOSBridgeClient(): VJSBridgeClient {
    return (window as any).webkit?.messageHandlers[CLIENT_BRIDGE_WINDOW_KEY];
  }

  private sendOperation(operationData: OperationData) {
    try {
      if (!this.bridgeClient) {
        console.log('Bridge is invalid. Now setting up new bridge client.');
        this.setupVJSBridgeClient();
        console.log(
          'Setup bridge client when sending operation data. Client set as: ',
          this.bridgeClient,
        );
      }
      const systemType = getSystemType();
      if (systemType === SystemType.Unknown) {
        console.error('Execute postMessage failed. Unknown system.');
      }
      // iOS下直接传对象，Android下先stringify后传JSON String
      const validOperationData: string | Record<string, any> =
        systemType === SystemType.iOS ? operationData : JSON.stringify(operationData);
      const result = this.bridgeClient?.postMessage(validOperationData);
      console.log('F2N > operationData: ', validOperationData, '\nType', typeof validOperationData);
      return result;
    } catch (error) {
      console.error('Execute postMessage failed. Client not ready\n', 'error: ', error);
    }
  }
}
